'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const adminNav = [
  { id: 'admin-dashboard', label: 'Dashboard', icon: 'HomeIcon', href: '/admin-panel', badge: null, section: 'main' },
  { id: 'admin-movies', label: 'Movie Catalog', icon: 'FilmIcon', href: '/admin-panel', badge: '3', section: 'content' },
  { id: 'admin-submissions', label: 'Submissions', icon: 'InboxArrowDownIcon', href: '/admin-panel', badge: '7', section: 'content' },
  { id: 'admin-reviews', label: 'Review Moderation', icon: 'StarIcon', href: '/admin-panel', badge: '12', section: 'content' },
  { id: 'admin-users', label: 'Users', icon: 'UserGroupIcon', href: '/admin-panel', badge: null, section: 'community' },
  { id: 'admin-contributors', label: 'Contributors', icon: 'PlusCircleIcon', href: '/admin-panel', badge: '3', section: 'community' },
  { id: 'admin-discussions', label: 'Discussions', icon: 'ChatBubbleLeftRightIcon', href: '/admin-panel', badge: null, section: 'community' },
  { id: 'admin-analytics', label: 'Analytics', icon: 'ChartBarIcon', href: '/admin-panel', badge: null, section: 'system' },
  { id: 'admin-settings', label: 'Settings', icon: 'Cog6ToothIcon', href: '/admin-panel', badge: null, section: 'system' },
];

const sections = [
  { id: 'main', label: null },
  { id: 'content', label: 'Content' },
  { id: 'community', label: 'Community' },
  { id: 'system', label: 'System' },
];

export default function AdminSidebar() {
  const [active, setActive] = useState('admin-dashboard');
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside className={`flex-shrink-0 bg-card border-r border-border flex flex-col transition-all duration-300 ease-smooth ${collapsed ? 'w-16' : 'w-60'} min-h-screen sticky top-0`}>
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 h-16 border-b border-border flex-shrink-0">
        <AppLogo size={28} />
        {!collapsed && (
          <div>
            <span className="font-sans text-base font-800 text-foreground">
              Cine<span className="text-gradient-red">world</span>
            </span>
            <span className="block text-xs text-primary font-600">Admin Panel</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto">
        {sections.map((section) => {
          const sectionLinks = adminNav.filter((l) => l.section === section.id);
          if (!sectionLinks.length) return null;
          return (
            <div key={`admin-section-${section.id}`} className="mb-4">
              {section.label && !collapsed && (
                <p className="px-2 mb-1.5 text-xs font-700 text-muted-foreground/60 uppercase tracking-widest">{section.label}</p>
              )}
              {sectionLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => setActive(link.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-500 transition-all duration-150 mb-0.5 ${
                    active === link.id ? 'sidebar-active' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                  }`}
                >
                  <Icon name={link.icon as Parameters<typeof Icon>[0]['name']} size={16} className="flex-shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{link.label}</span>
                      {link.badge && (
                        <span className="text-xs font-700 bg-primary/15 text-primary px-1.5 py-0.5 rounded-full">{link.badge}</span>
                      )}
                    </>
                  )}
                </button>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border px-2 py-3 flex-shrink-0 space-y-1">
        <Link href="/" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors`}>
          <Icon name="ArrowLeftIcon" size={16} className="flex-shrink-0" />
          {!collapsed && 'Back to Site'}
        </Link>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
        >
          <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={14} className="flex-shrink-0" />
          {!collapsed && 'Collapse'}
        </button>
      </div>
    </aside>
  );
}